import { useQuery } from '@tanstack/react-query';
import { Apple, FlaskConical, Pencil, Smartphone, X } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { CopyButton } from '@/components/shared/copy-button';
import { LoadingButton } from '@/components/shared/loading-button';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import type { Installation, NotificationPermission } from '@/lib/types';

const PERMISSION_TONE: Record<NotificationPermission, 'green' | 'amber' | 'red' | 'slate'> = {
  authorized: 'green', provisional: 'amber', denied: 'red', not_determined: 'slate',
};

/** API values are snake_case; show them as sentence case ("not_determined" → "Not determined"). */
export const humanize = (s: string) => (s.charAt(0).toUpperCase() + s.slice(1)).replaceAll('_', ' ');

export function PermissionBadge({ value }: { value: NotificationPermission | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <ToneBadge tone={PERMISSION_TONE[value]}>{humanize(value)}</ToneBadge>;
}

/** Active, or why the device was deactivated ("Invalid token", "User removed", …). */
export function DeviceStatusBadge({ device }: { device: Pick<Installation, 'is_active' | 'deactivation_reason'> }) {
  return device.is_active
    ? <ToneBadge tone="green" dot>Active</ToneBadge>
    : <ToneBadge tone="slate">{humanize(device.deactivation_reason ?? 'inactive')}</ToneBadge>;
}

const PlatformIcon = ({ platform, className = 'size-3.5' }: { platform: Installation['platform']; className?: string }) =>
  platform === 'ios' ? <Apple className={className} /> : <Smartphone className={className} />;

/**
 * Platform, model and test-device badge together, as the Devices table shows a device. `nameFirst` (pickers of
 * test devices) leads with the test-device name, in bold, since that's what people know their phones by.
 */
export function DeviceCell({ device: d, nameFirst }: { device: Installation; nameFirst?: boolean }) {
  if (nameFirst && d.test_device_name) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="grid size-7 shrink-0 place-items-center rounded-md bg-muted"><PlatformIcon platform={d.platform} /></span>
        <span className="grid gap-0.5 leading-tight">
          <span className="font-semibold text-foreground">{d.test_device_name}</span>
          <span className="text-xs text-muted-foreground">
            {[d.device_model, d.platform === 'ios' ? 'iOS' : 'Android'].filter(Boolean).join(' · ')}
          </span>
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="grid size-7 shrink-0 place-items-center rounded-md bg-muted"><PlatformIcon platform={d.platform} /></span>
      <span className="grid gap-0.5 leading-tight">
        <span className="inline-flex items-center gap-2">
          {d.device_model ?? <span className="text-muted-foreground">Unknown model</span>}
          {d.is_test_device && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="grid size-5 place-items-center rounded-full bg-primary/10 text-primary" aria-label="Test device">
                  <FlaskConical className="size-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Test device{d.test_device_name ? `: ${d.test_device_name}` : ''}</TooltipContent>
            </Tooltip>
          )}
        </span>
        <span className="text-xs text-muted-foreground">
          {d.platform === 'ios' ? 'iOS' : 'Android'}
          {d.is_test_device && d.test_device_name && <> · <span className="text-foreground">{d.test_device_name}</span></>}
        </span>
      </span>
    </span>
  );
}

/** Everything stored for one device. Opens from a Devices row; refetched so it reflects the latest state. */
export function DeviceSheet({ appId, device, onClose, onNameTest, onRemoveTest, removing }: {
  appId: string; device: Installation | null; onClose(): void;
  onNameTest(d: Installation): void; onRemoveTest(d: Installation): void; removing: boolean;
}) {
  const q = useQuery({
    // Under ['installations', appId] so renaming or removing a test device refreshes it too.
    queryKey: ['installations', appId, 'detail', device?.installation_id],
    enabled: !!device,
    queryFn: () => api<Installation>(`/admin/v1/apps/${appId}/installations/${device!.installation_id}`),
    placeholderData: device ?? undefined,
  });
  const d = q.data ?? device;
  const tags = Object.entries(d?.tags ?? {});

  return (
    <Sheet open={!!device} onOpenChange={(o) => !o && onClose()}>
      {/* Sized to fit a normal laptop screen without scrolling; on a short window it still scrolls, and the
          sections are shrink-0 so the browser never squeezes or clips them. */}
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader className="shrink-0 border-b px-6 py-4 pr-12">
          <div className="flex items-center gap-3">
            {d && <span className="grid size-10 shrink-0 place-items-center rounded-lg border bg-muted"><PlatformIcon platform={d.platform} className="size-5" /></span>}
            <div className="grid min-w-0 gap-0.5">
              <SheetTitle className="truncate text-base">{d?.device_model ?? 'Unknown model'}</SheetTitle>
              <SheetDescription className="truncate">
                {d?.platform === 'ios' ? 'iOS' : 'Android'}{d?.os_version && ` ${d.os_version}`}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {d && (
          <div className="shrink-0 px-6">
            <Section title="Overview">
              <Row k="Status">
                <DeviceStatusBadge device={d} />
              </Row>
              <Row k="Permission"><PermissionBadge value={d.notification_permission} /></Row>
              {d.platform === 'ios' && (
                <Row k="APNs gateway">
                  {d.apns_sandbox === null ? <Muted>Unknown</Muted> : d.apns_sandbox ? 'Sandbox (Xcode build)' : 'Production (TestFlight / App Store)'}
                </Row>
              )}
              <Row k="Test device">
                {d.is_test_device
                  ? <span className="inline-flex items-center gap-1.5"><FlaskConical className="size-3.5 text-primary" /> {d.test_device_name ?? 'Yes'}</span>
                  : <Muted>No</Muted>}
              </Row>
              <Row k="Failed sends in a row"><span className="tabular-nums">{d.consecutive_failures}</span></Row>
            </Section>

            <Section title="Identity">
              <Row k="Installation ID"><Mono copy={d.installation_id} nowrap>{d.installation_id}</Mono></Row>
              <Row k="External User ID">{d.external_user_id ? <Mono copy={d.external_user_id}>{d.external_user_id}</Mono> : <Muted>—</Muted>}</Row>
              <Row k="Push token"><Mono>{d.token_preview}</Mono></Row>
            </Section>

            <Section title="Device">
              <Row k="OS version">{d.os_version ?? <Muted>—</Muted>}</Row>
              <Row k="App version">{d.app_version ?? <Muted>—</Muted>}</Row>
              <Row k="SDK version">{d.sdk_version ?? <Muted>—</Muted>}</Row>
              <Row k="Language">{d.language ?? <Muted>—</Muted>}</Row>
              <Row k="Timezone">{d.timezone ?? <Muted>—</Muted>}</Row>
            </Section>

            <Section title="Tags">
              {tags.length === 0 ? <Row k="Tags"><Muted>None</Muted></Row> : tags.map(([k, v]) => <Row key={k} k={k}>{v}</Row>)}
            </Section>

            <Section title="Activity" last>
              <Row k="First registered">{fmtDate(d.created_at)}</Row>
              <Row k="Last seen">{fmtDate(d.last_seen_at)}</Row>
              {!d.is_active && <Row k="Deactivated">{fmtDate(d.deactivated_at)}</Row>}
            </Section>
          </div>
        )}

        {d && (
          <SheetFooter className="sticky bottom-0 mt-auto shrink-0 flex-row justify-end gap-2 border-t bg-background px-6 py-3">
            {d.is_test_device ? (
              <>
                <LoadingButton variant="outline" loading={removing} onClick={() => onRemoveTest(d)}><X /> Remove from test devices</LoadingButton>
                <Button variant="outline" onClick={() => onNameTest(d)}><Pencil /> Rename</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => onNameTest(d)}><FlaskConical /> Add as test device</Button>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Section = ({ title, last, children }: { title: string; last?: boolean; children: ReactNode }) => (
  <Fragment>
    <section className="py-4">
      <h3 className="mb-2.5 text-sm font-medium">{title}</h3>
      <dl className="grid gap-y-2 text-sm">{children}</dl>
    </section>
    {!last && <Separator />}
  </Fragment>
);

const Row = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="grid min-h-6 grid-cols-[9rem_1fr] items-center gap-4">
    <dt className="text-muted-foreground">{k}</dt>
    <dd className="min-w-0">{children}</dd>
  </div>
);

const Muted = ({ children }: { children: ReactNode }) => <span className="text-muted-foreground">{children}</span>;

// `nowrap` keeps a fixed-length id (a UUID) on one line; free-form values still wrap.
const Mono = ({ children, copy, nowrap }: { children: ReactNode; copy?: string; nowrap?: boolean }) => (
  <span className="inline-flex max-w-full items-center gap-2">
    <span className={nowrap ? 'font-mono text-xs whitespace-nowrap' : 'font-mono text-xs break-all'}>{children}</span>
    {copy && <CopyButton value={copy} label={false} />}
  </span>
);
